<!-- app-subdomain-creators/src/Components/CourseVideoPlayer.vue -->
<template>
  <div
    class="relative w-full h-full bg-transparent overflow-hidden flex items-center justify-center group"
    :style="playerStyleVars"
    @mousemove="onRootMouseMove"
    @mouseleave="hideUiDelayed"
  >
    <!-- VIDEO -->
    <video
      ref="videoEl"
      :poster="props.posterUrl || ''"
      class="block w-full h-full object-contain bg-transparent border-0 outline-none ring-0 shadow-none focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
      :style="{ borderRadius: 'var(--player-radius, 16px)', border: 'none', outline: 'none' }"
      playsinline
      preload="auto"
      @timeupdate="updateProgress"
      @loadedmetadata="setDuration"
      @loadeddata="onVideoLoaded"
      @ended="onVideoEnded"
      @click="togglePlay"
    />

    <div
      v-if="showPosterLayer"
      class="absolute inset-0 z-10 pointer-events-none"
      :style="{ borderRadius: 'var(--player-radius, 16px)' }"
    >
      <img
        :src="props.posterUrl"
        alt="Video thumbnail"
        class="w-full h-full object-cover"
        @load="posterImageLoaded = true"
        @error="posterImageLoaded = true"
      >
    </div>

    <div
      v-if="statusMessage"
      class="absolute inset-x-0 bottom-4 z-30 flex justify-center pointer-events-none"
    >
      <div class="px-3 py-1.5 rounded-full bg-black/65 text-white text-xs font-medium">
        {{ statusMessage }}
      </div>
    </div>


    <!-- LOADING OVERLAY (WITH FADE) -->
    <transition name="loading-fade">
      <div
        v-if="showLoadingOverlay"
        class="absolute inset-0 flex items-center justify-center bg-zinc-200/70"
        :style="{ borderRadius: 'var(--player-radius, 16px)' }"
      >
        <div
          class="absolute inset-0 skeleton-shimmer"
          :style="{ borderRadius: 'var(--player-radius, 16px)' }"
        />
      </div>
    </transition>


    <!-- BIG CENTER PLAY BUTTON -->
    <div
      v-if="isPaused && showOverlay && !isLoading"
      class="absolute inset-0 z-20 flex items-center justify-center cursor-pointer transition-opacity duration-300"
      @click.stop="togglePlay"
    >
      <div
        class="flex items-center p-5 rounded-full opacity-70 transition"
        :style="bigPlayButtonStyle"
      >
        <PlayIcon class="text-white/70 w-16 h-16 mx-auto pl-2" />
      </div>
    </div>

    <!-- CONTROL BAR -->
    <transition name="fade">
      <div
        v-if="hasStartedPlayback && showControls && !isLoading"
        class="absolute bottom-0 left-0 right-0 px-4 pt-8 pb-1 flex flex-col gap-2"
        @mouseenter="onControlsEnter"
        @mouseleave="onControlsLeave"
      >
        <!-- TIMELINE + HOVER PREVIEW -->
        <div class="relative w-full">
          <!-- Hover preview bubble -->
          <transition name="fade">
            <div
              v-if="isHoveringBar"
              class="absolute -top-20"
              :style="{ left: hoverPercent + '%' }"
              style="transform: translateX(-50%);"
            >
              <div class="flex flex-col items-center">
                <!-- Placeholder preview box (can later become a real frame) -->
                <div
                  class="w-40 h-24 bg-black/90 border border-white/10 rounded-lg flex items-center justify-center text-xs text-white/70"
                >
                  {{ hoverFormattedTime }}
                </div>
                <div
                  class="mt-1 px-2 py-0.5 rounded-full bg-black/90 text-[10px] text-white/80"
                >
                  {{ hoverFormattedTime }}
                </div>
              </div>
            </div>
          </transition>

          <!-- Progress bar -->
          <div
            ref="progressBarEl"
            class="relative w-full h-2 bg-white/20 rounded-full cursor-pointer overflow-hidden"
            @mousemove="onBarMouseMove"
            @mouseleave="onBarMouseLeave"
            @mousedown.prevent="onBarMouseDown"
          >
            <!-- Hover indicator line -->
            <div
              v-if="isHoveringBar"
              class="absolute top-0 bottom-0 w-[2px] bg-white/40"
              :style="{ left: hoverPercent + '%' }"
            />

            <!-- Played progress -->
            <div
              class="absolute top-0 left-0 h-2 rounded-full transition-all"
              :style="{ width: progress + '%', backgroundColor: normalizedControlsColor }"
            />
          </div>
        </div>


        <!-- BOTTOM ROW — YouTube style pill controls -->
        <div class="flex items-center justify-between text-white text-[13px] mt-2 select-none">
          <!-- LEFT SIDE -->
          <div class="flex items-center gap-2">
            <!-- PLAY BUTTON -->
            <button
              class="p-3 rounded-full bg-black/50 hover:bg-black/70 transition flex items-center justify-center"
              @click="togglePlay"
            >
              <PlayIcon
                v-if="isPaused"
                class="text-white/90 w-8 h-8"
              />
              <PauseIcon
                v-else
                class="text-white/90 w-8 h-8"
              />
            </button>

            <!-- VOLUME GROUP -->
            <div class="flex items-center gap-2 px-3 py-2 rounded-full bg-black/50">
              <button
                class="control-accent-hover transition"
                @click="toggleMute"
              >
                <SpeakerXMarkIcon
                  v-if="isMuted || volume === 0"
                  class="text-white/90 w-7 h-7"
                />
                <SpeakerWaveIcon
                  v-else
                  class="text-white/90 w-7 h-7"
                />
              </button>

              <input
                v-model.number="volume"
                type="range"
                min="0"
                max="1"
                step="0.05"
                class="w-20 cursor-pointer bg-transparent border-none
                      focus:outline-none focus:ring-0 focus:border-transparent appearance-none"
                :style="volumeInputStyle"
                @input="onVolumeChange"
              >
            </div>

            <!-- TIME DISPLAY -->
            <div class="px-3 py-2 rounded-full bg-black/50 text-sm">
              {{ formattedTime }} / {{ formattedDuration }}
            </div>
          </div>

          <!-- RIGHT SIDE -->
          <div class="flex items-center gap-2">
            <!-- FULLSCREEN -->
            <!-- <button
      @click="toggleFullscreen"
      class="p-3 rounded-full bg-black/50 hover:bg-black/70 transition flex items-center justify-center"
    >
      <ArrowsPointingOutIcon class="text-white/90 w-7 h-7" />
    </button> -->
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed, onUnmounted, watch } from "vue";
import Hls from "hls.js";

import {
  PlayIcon,
  PauseIcon,
  SpeakerWaveIcon,
  SpeakerXMarkIcon,
  // ArrowsPointingOutIcon,
} from "@heroicons/vue/24/solid";

const normalizeApiBaseUrl = (value) => {
  const raw = String(value ?? "").trim();
  if (!raw || raw.toLowerCase() === "undefined" || raw.toLowerCase() === "null") return "";
  return raw.replace(/\/+$/, "");
};
const apiProxyUrl = normalizeApiBaseUrl(process.env.VUE_APP_API_PROXY_URL);
const normalizePlaylistProxyUrl = (value) => {
  const raw = String(value || "").trim();
  if (!raw || /^undefined(?:\/|$)/i.test(raw) || /^null(?:\/|$)/i.test(raw)) return "";
  const match = raw.match(/\/(?:api\/)?(?:media\/)?stream-hls\/playlist\/([^/?#]+)/i);
  if (!match?.[1]) return raw;
  const fileId = String(decodeURIComponent(match[1]) || "").trim();
  if (!fileId) return raw;
  return `${apiProxyUrl || ""}/api/stream-hls/playlist/${encodeURIComponent(fileId)}`;
};
const emit = defineEmits(["loading-change"]);

const props = defineProps({
  fileId: { type: String, required: true },
  playlistUrl: { type: String, default: "" },
  posterUrl: { type: String, default: "" },
  sourceKey: { type: String, default: "" },
  controlsColor: { type: String, default: "#2D8CFF" },
  borderRadius: { type: Number, default: 16 },
  // 👇 NEW: tells the player to use the chat HLS route instead of media-drive route
  chatMode: { type: Boolean, default: false },
});

const videoEl = ref(null);
const progressBarEl = ref(null);

let hlsInstance = null;
let retryTimer = null;
let detachVideoDiagnostics = null;

const HLS_VOD_BUFFER_CONFIG = {
  maxBufferLength: 60,
  maxMaxBufferLength: 120,
  maxBufferSize: 90 * 1000 * 1000,
  backBufferLength: 45,
  manifestLoadingMaxRetry: 6,
  levelLoadingMaxRetry: 6,
  fragLoadingMaxRetry: 8,
  fragLoadingRetryDelay: 1000,
  fragLoadingMaxRetryTimeout: 10000,
};

const volume = ref(1);
const isMuted = ref(false);
const statusMessage = ref("");
const isPlaybackDiagnosticsEnabled = true;
const normalizeColor = (value, fallback = "#2D8CFF") => {
  const raw = String(value || "").trim();
  const withHash = raw.startsWith("#") ? raw : `#${raw}`;
  if (/^#[0-9a-fA-F]{6}$/.test(withHash)) return withHash.toUpperCase();
  return fallback;
};
const normalizedControlsColor = computed(() =>
  normalizeColor(props.controlsColor, "#2D8CFF")
);
const normalizedBorderRadius = computed(() => {
  const value = Number(props.borderRadius ?? 16);
  if (!Number.isFinite(value)) return 16;
  return Math.max(0, Math.min(120, Math.round(value)));
});
const playerStyleVars = computed(() => ({
  "--player-control-accent": normalizedControlsColor.value,
  "--player-radius": `${normalizedBorderRadius.value}px`,
  borderRadius: `${normalizedBorderRadius.value}px`,
}));
const bigPlayButtonStyle = computed(() => ({
  backgroundColor: normalizedControlsColor.value,
}));

const volumeTrackStyle = computed(() => ({
  '--volume-percent': `${volume.value * 100}%`,
}));
const volumeInputStyle = computed(() => ({
  ...volumeTrackStyle.value,
  accentColor: normalizedControlsColor.value,
}));

const getBufferAheadSeconds = (video) => {
  if (!video || !video.buffered || video.buffered.length === 0) return 0;
  const current = Number(video.currentTime || 0);
  for (let i = 0; i < video.buffered.length; i += 1) {
    const start = Number(video.buffered.start(i) || 0);
    const end = Number(video.buffered.end(i) || 0);
    if (current >= start && current <= end) return Math.max(0, end - current);
  }
  return 0;
};

const logPlaybackDiagnostic = (label, payload = {}) => {
  if (!isPlaybackDiagnosticsEnabled) return;
  console.debug("[CourseVideoPlayer][creator]", label, payload);
};

const setupVideoDiagnostics = (video) => {
  if (!video) return () => {};

  const onWaiting = () => {
    logPlaybackDiagnostic("video:waiting", {
      currentTime: Number((video.currentTime || 0).toFixed(3)),
      bufferAheadSeconds: Number(getBufferAheadSeconds(video).toFixed(3)),
      readyState: video.readyState,
      networkState: video.networkState,
    });
  };

  const onStalled = () => {
    logPlaybackDiagnostic("video:stalled", {
      currentTime: Number((video.currentTime || 0).toFixed(3)),
      bufferAheadSeconds: Number(getBufferAheadSeconds(video).toFixed(3)),
      readyState: video.readyState,
      networkState: video.networkState,
    });
  };

  const onCanPlay = () => {
    logPlaybackDiagnostic("video:canplay", {
      currentTime: Number((video.currentTime || 0).toFixed(3)),
      bufferAheadSeconds: Number(getBufferAheadSeconds(video).toFixed(3)),
    });
  };

  const onPlaying = () => {
    logPlaybackDiagnostic("video:playing", {
      currentTime: Number((video.currentTime || 0).toFixed(3)),
      bufferAheadSeconds: Number(getBufferAheadSeconds(video).toFixed(3)),
    });
  };

  video.addEventListener("waiting", onWaiting);
  video.addEventListener("stalled", onStalled);
  video.addEventListener("canplay", onCanPlay);
  video.addEventListener("playing", onPlaying);

  return () => {
    video.removeEventListener("waiting", onWaiting);
    video.removeEventListener("stalled", onStalled);
    video.removeEventListener("canplay", onCanPlay);
    video.removeEventListener("playing", onPlaying);
  };
};


/* -------------------------------------------------
   HLS INITIALIZATION
-------------------------------------------------- */
function loadVideoSource(playlistUrl) {
  const video = videoEl.value;
  if (!video || !playlistUrl) return;

  // show spinner for a new load
  setLoadingState(true);
  statusMessage.value = "";
  clearRetryTimer();

  // clean previous HLS instance
  if (hlsInstance) {
    hlsInstance.destroy();
    hlsInstance = null;
  }

  // hard reset previous media so stale video from another instance cannot remain visible
  video.pause();
  video.removeAttribute("src");
  video.load();

  // Native HLS devices (iOS/Safari)
  if (video.canPlayType("application/vnd.apple.mpegurl")) {
    video.src = playlistUrl;
    // <video @loadeddata="onVideoLoaded"> will hide spinner
    video.load();
    return;
  }

  // Other browsers (Hls.js)
  if (Hls.isSupported()) {
    const fragStartedAtMs = new Map();
    const hls = new Hls({
      debug: false,
      ...HLS_VOD_BUFFER_CONFIG,
      lowLatencyMode: false,
      capLevelToPlayerSize: true,
      startFragPrefetch: true,

      xhrSetup(xhr) {
        // propagate cookies / JWT through proxy
        xhr.withCredentials = true;
      },
    });

    hlsInstance = hls;

    // Prefer lowest level first for fastest start
    hls.startLevel = 0;

    hls.loadSource(playlistUrl);
    hls.attachMedia(video);

    // As soon as any data is buffered, hide the spinner
    hls.on(Hls.Events.BUFFER_APPENDED, () => {
      if (isLoading.value) setLoadingState(false);
    });

    // Keep MANIFEST_PARSED simple; Hls will load first segment
    hls.on(Hls.Events.MANIFEST_PARSED, () => {
      // no video.load() here – it can cancel segment requests on some browsers
    });

    hls.on(Hls.Events.MANIFEST_PARSED, (_event, data) => {
      logPlaybackDiagnostic("hls:manifestParsed", {
        levels: Array.isArray(data?.levels) ? data.levels.length : 0,
      });
    });

    hls.on(Hls.Events.FRAG_LOADING, (_event, data) => {
      const sn = data?.frag?.sn;
      const key = sn == null ? null : String(sn);
      if (!key) return;
      fragStartedAtMs.set(key, Date.now());
    });
    hls.on(Hls.Events.FRAG_LOADED, (_event, data) => {
      const sn = data?.frag?.sn;
      const key = sn == null ? null : String(sn);
      if (!key) return;
      const startedAt = fragStartedAtMs.get(key);
      if (!startedAt) return;
      fragStartedAtMs.delete(key);
      logPlaybackDiagnostic("hls:fragLoaded", {
        sn: key,
        ms: Date.now() - startedAt,
        level: Number(data?.frag?.level ?? -1),
        durationSeconds: Number(data?.frag?.duration || 0),
        sizeBytes: Number(data?.stats?.total || 0),
        bufferAheadSeconds: Number(getBufferAheadSeconds(video).toFixed(3)),
      });
    });

    hls.on(Hls.Events.ERROR, (_event, data) => {
      console.warn("[HLS ERROR]", data);
      if (data?.fatal || data?.details === Hls.ErrorDetails.BUFFER_STALLED_ERROR) {
        logPlaybackDiagnostic("hls:error", {
          fatal: Boolean(data?.fatal),
          type: String(data?.type || ""),
          details: String(data?.details || ""),
          code: Number(data?.response?.code || 0),
          bufferAheadSeconds: Number(getBufferAheadSeconds(video).toFixed(3)),
          currentTime: Number((video.currentTime || 0).toFixed(3)),
        });
      }

      const statusCode = Number(data?.response?.code || 0);
      const responseText = String(data?.response?.text || "");
      const notReadyYet =
        statusCode === 404 ||
        statusCode === 500 ||
        /NoSuchKey/i.test(responseText);

      if (!data?.fatal) {
        if (data?.details === Hls.ErrorDetails.BUFFER_STALLED_ERROR) {
          hls.startLoad();
        }
        return;
      }

      if (data.fatal) {
        if (notReadyYet) {
          statusMessage.value = "Video is processing. It will play automatically when ready.";
          scheduleReload(playlistUrl);
          return;
        }

        switch (data.type) {
          case Hls.ErrorTypes.NETWORK_ERROR:
            hls.startLoad();
            break;
          case Hls.ErrorTypes.MEDIA_ERROR:
            hls.recoverMediaError();
            break;
          default:
            hls.destroy();
            break;
        }
      }
    });
  } else {
    console.error("HLS unsupported in this browser.");
  }
}

/* -------------------------------------------------
   PREFLIGHT / PLAYLIST PREP (KEEPS JWT FLOW)
   NOTE: returns URL immediately, fetch is fire-and-forget
-------------------------------------------------- */
async function prepareHlsRequest(fileId, explicitUrl) {
  const normalizedExplicit = normalizePlaylistProxyUrl(explicitUrl);
  if (
    normalizedExplicit &&
    !/^undefined(?:\/|$)/i.test(normalizedExplicit) &&
    !/^null(?:\/|$)/i.test(normalizedExplicit)
  ) {
    return normalizedExplicit;
  }

  const normalizedFileId = String(fileId || "").trim();
  if (!normalizedFileId) return "";

  // 👇 KEY CHANGE:
  // - For chat videos: use /api/chat-videos/master/:fileId
  // - For media-drive videos: use /api/stream-hls/playlist/:fileId
  const basePath = props.chatMode
    ? "/api/chat-videos/master/"
    : "/api/stream-hls/playlist/";

  const url = `${apiProxyUrl || ""}${basePath}${encodeURIComponent(normalizedFileId)}`;
  return url;
}

function clearRetryTimer() {
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
}

function scheduleReload(playlistUrl) {
  clearRetryTimer();
  retryTimer = setTimeout(() => {
    loadVideoSource(playlistUrl);
  }, 4000);
}

/* -------------------------------------------------
   WATCHER — waits for BOTH fileId AND videoEl
-------------------------------------------------- */
watch(
  [() => props.fileId, () => props.playlistUrl, () => props.sourceKey, videoEl],
  async ([id, explicitUrl, sourceKey, video]) => {
    if (!video) return;
    void sourceKey;

    const playlistUrl = await prepareHlsRequest(id, explicitUrl);
    if (!playlistUrl) return;

    loadVideoSource(playlistUrl);
  },
  { immediate: true }
);

/* -------------------------------------------------
   ORIGINAL PLAYER LOGIC
-------------------------------------------------- */

const isPaused = ref(true);
const isLoading = ref(true);
const hasStartedPlayback = ref(false);
const posterImageLoaded = ref(false);

const duration = ref(0);
const currentTime = ref(0);
const progress = ref(0);

const showControls = ref(true);
const showOverlay = ref(true);

const isHoveringBar = ref(false);
const hoverPercent = ref(0);
const hoverTime = ref(0);

const isDragging = ref(false);
const pointerInsideControls = ref(false);

let hideTimeout = null;
let mouseUpListener = null;

function setLoadingState(next) {
  const value = Boolean(next);
  if (isLoading.value === value) return;
  isLoading.value = value;
  emit("loading-change", value);
}

// When first frame loads (bound via @loadeddata on <video>)
function onVideoLoaded() {
  setLoadingState(false);
  statusMessage.value = "";

  if (videoEl.value) {
    videoEl.value.volume = volume.value;
  }
}

// Time formatting
const format = (n) => {
  if (!n || Number.isNaN(n)) return "0:00";
  const m = Math.floor(n / 60);
  const s = Math.floor(n % 60)
    .toString()
    .padStart(2, "0");
  return `${m}:${s}`;
};

const formattedTime = computed(() => format(currentTime.value));
const formattedDuration = computed(() => format(duration.value));
const hoverFormattedTime = computed(() => format(hoverTime.value));
const showPosterLayer = computed(
  () =>
    Boolean(props.posterUrl) &&
    isPaused.value &&
    !hasStartedPlayback.value
);
const showLoadingOverlay = computed(
  () =>
    isLoading.value &&
    (!showPosterLayer.value || (showPosterLayer.value && !posterImageLoaded.value))
);

watch(
  () => props.posterUrl,
  (next) => {
    posterImageLoaded.value = !next;
  },
  { immediate: true }
);

watch(
  videoEl,
  (node) => {
    if (typeof detachVideoDiagnostics === "function") {
      detachVideoDiagnostics();
      detachVideoDiagnostics = null;
    }
    if (node) {
      detachVideoDiagnostics = setupVideoDiagnostics(node);
    }
  },
  { immediate: true }
);

watch(
  () => props.sourceKey,
  () => {
    const video = videoEl.value;
    if (video) {
      video.pause();
      video.currentTime = 0;
    }
    setLoadingState(true);
    isPaused.value = true;
    hasStartedPlayback.value = false;
    showOverlay.value = true;
    showControls.value = true;
    currentTime.value = 0;
    progress.value = 0;
    isHoveringBar.value = false;
    isDragging.value = false;
    pointerInsideControls.value = false;
    statusMessage.value = "";
    clearTimeout(hideTimeout);
  }
);

// Play/Pause toggle
function togglePlay() {
  const video = videoEl.value;
  if (!video) return;

  if (video.paused) {
    Promise.resolve(video.play())
      .then(() => {
        isPaused.value = false;
        showOverlay.value = false;
        hasStartedPlayback.value = true;
      })
      .catch(() => {
        statusMessage.value = "Video unavailable.";
        isPaused.value = true;
      });
  } else {
    video.pause();
    isPaused.value = true;
    showOverlay.value = true;
  }

  showUi();
}

function onVideoEnded() {
  const video = videoEl.value;
  if (!video) return;

  video.pause();
  video.currentTime = 0;
  isPaused.value = true;
  showOverlay.value = true;
  hasStartedPlayback.value = false;
  currentTime.value = 0;
  progress.value = 0;
  showControls.value = false;
  pointerInsideControls.value = false;
  isHoveringBar.value = false;
  isDragging.value = false;
  clearTimeout(hideTimeout);
}

// Progress tracking
function updateProgress() {
  if (!videoEl.value || !duration.value) return;
  currentTime.value = videoEl.value.currentTime;
  progress.value = (currentTime.value / duration.value) * 100;
}

function setDuration() {
  if (!videoEl.value) return;
  duration.value = videoEl.value.duration || 0;
}

function seekToPercent(percent) {
  const video = videoEl.value;
  if (!video || !duration.value) return;

  const clamped = Math.min(1, Math.max(0, percent));
  video.currentTime = clamped * duration.value;
  currentTime.value = video.currentTime;
  progress.value = clamped * 100;
}

// Hover behavior
function updateHoverFromEvent(e) {
  if (!progressBarEl.value || !duration.value) return;

  const rect = progressBarEl.value.getBoundingClientRect();
  let percent = (e.clientX - rect.left) / rect.width;
  percent = Math.min(1, Math.max(0, percent));

  hoverPercent.value = percent * 100;
  hoverTime.value = percent * duration.value;
}

function onBarMouseMove(e) {
  if (!duration.value) return;
  isHoveringBar.value = true;
  updateHoverFromEvent(e);

  if (isDragging.value) {
    seekToPercent(hoverPercent.value / 100);
  }
}

function onBarMouseLeave() {
  isHoveringBar.value = false;

  if (!pointerInsideControls.value) {
    hideUiDelayed();
  }
}

function onBarMouseDown(e) {
  if (!duration.value) return;

  isDragging.value = true;
  updateHoverFromEvent(e);
  seekToPercent(hoverPercent.value / 100);
  showUi();

  mouseUpListener = () => {
    isDragging.value = false;
    window.removeEventListener("mouseup", mouseUpListener);
    mouseUpListener = null;

    if (!pointerInsideControls.value) {
      hideUiDelayed();
    }
  };

  window.addEventListener("mouseup", mouseUpListener);
}

// Volume
function onVolumeChange() {
  const video = videoEl.value;
  if (!video) return;

  video.muted = false;
  isMuted.value = false;
  video.volume = volume.value;
}

function toggleMute() {
  const video = videoEl.value;
  if (!video) return;

  if (isMuted.value || volume.value === 0) {
    if (volume.value === 0) volume.value = 0.5;
    video.muted = false;
    isMuted.value = false;
    video.volume = volume.value;
  } else {
    video.muted = true;
    isMuted.value = true;
  }
}

// Autohide UI
function showUi() {
  if (!hasStartedPlayback.value) return;

  showControls.value = true;
  clearTimeout(hideTimeout);

  hideTimeout = setTimeout(() => {
    if (
      !isDragging.value &&
      !isHoveringBar.value &&
      !pointerInsideControls.value
    ) {
      showControls.value = false;
    }
  }, 2000);
}

function hideUiDelayed() {
  if (!hasStartedPlayback.value) return;

  clearTimeout(hideTimeout);

  hideTimeout = setTimeout(() => {
    if (
      !isDragging.value &&
      !isHoveringBar.value &&
      !pointerInsideControls.value
    ) {
      showControls.value = false;
    }
  }, 1500);
}

function onControlsEnter() {
  if (!hasStartedPlayback.value) return;

  pointerInsideControls.value = true;
  clearTimeout(hideTimeout);
  showControls.value = true;
}

function onControlsLeave() {
  if (!hasStartedPlayback.value) return;

  pointerInsideControls.value = false;

  if (!isHoveringBar.value && !isDragging.value) {
    hideUiDelayed();
  }
}

function onRootMouseMove() {
  if (!hasStartedPlayback.value) return;
  showUi();
}

// Cleanup
onUnmounted(() => {
  clearRetryTimer();
  if (typeof detachVideoDiagnostics === "function") {
    detachVideoDiagnostics();
    detachVideoDiagnostics = null;
  }
  if (mouseUpListener) {
    window.removeEventListener("mouseup", mouseUpListener);
  }
  clearTimeout(hideTimeout);

  if (hlsInstance) {
    hlsInstance.destroy();
    hlsInstance = null;
  }
});
</script>

<style scoped>
/* Fade animation for controls */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Fade animation for loading overlay */
.loading-fade-enter-active,
.loading-fade-leave-active {
  transition: opacity 0.3s ease;
}

.loading-fade-enter-from,
.loading-fade-leave-to {
  opacity: 0;
}

.control-accent-hover:hover {
  color: var(--player-control-accent);
}

.skeleton-shimmer {
  background: linear-gradient(
    110deg,
    rgba(255, 255, 255, 0.32) 8%,
    rgba(255, 255, 255, 0.68) 18%,
    rgba(255, 255, 255, 0.32) 33%
  );
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.05s linear infinite;
}

@keyframes skeleton-shimmer {
  to {
    background-position-x: -200%;
  }
}

/* Range input basic reset (keeps Tailwind accent color) */
input[type='range'] {
  background: transparent;
}

/* ─────────────────────────────────────────────
   Custom <input type="range"> styling
   (used for volume in the chat video player)
────────────────────────────────────────────── */

/* Base range reset */
input[type='range'] {
  -webkit-appearance: none;
  appearance: none;
  background: transparent;
}

/* WebKit track with dynamic fill using CSS variable */
input[type='range']::-webkit-slider-runnable-track {
  height: 4px;
  border-radius: 9999px;
  background: linear-gradient(
    to right,
    var(--player-control-accent, #2d8cff) 0%,
    var(--player-control-accent, #2d8cff) var(--volume-percent, 50%),
    rgba(255, 255, 255, 0.25) var(--volume-percent, 50%),
    rgba(255, 255, 255, 0.25) 100%
  );
}

/* WebKit thumb */
input[type='range']::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 12px;
  height: 12px;
  border-radius: 9999px;
  background: var(--player-control-accent, #2d8cff);
  margin-top: -4px; /* centers thumb on 4px track */
}

/* Firefox track */
input[type='range']::-moz-range-track {
  height: 4px;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.25);
}

/* Firefox filled portion */
input[type='range']::-moz-range-progress {
  height: 4px;
  border-radius: 9999px;
  background: var(--player-control-accent, #2d8cff);
}

/* Firefox thumb */
input[type='range']::-moz-range-thumb {
  width: 12px;
  height: 12px;
  border-radius: 9999px;
  background: var(--player-control-accent, #2d8cff);
  border: none;
}
</style>

